/**
 * 用户状态管理
 *
 * 功能：
 * - 用户登录/登出
 * - Token 管理（access token + refresh token）
 * - 用户信息管理
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";

import { getAdminAuth } from "@/api/gen/admin-auth";
import { adminLoginBody } from "@/api/gen/zod/admin-auth/admin-auth.zod";
import type {
  AuthV1AdminUserInfo,
  AuthV1AdminLoginRes,
} from "@/api/gen/models";
import { encryptPassword } from "@/utils/crypto";

/**
 * 用户信息接口
 */
export interface UserInfo {
  id: number;
  username: string;
  nickname: string;
  email?: string;
  phone?: string;
  avatar?: string;
  roles?: string[];
  permissions?: string[];
}

/**
 * 登录响应接口
 */
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: UserInfo;
}

export const useUserStore = defineStore(
  "user",
  () => {
    // 状态
    const token = ref<string>("");
    const refreshToken = ref<string>("");
    const userInfo = ref<UserInfo | null>(null);

    // 计算属性
    const isLoggedIn = computed(() => !!token.value);
    const roles = computed(() => userInfo.value?.roles || []);
    const permissions = computed(() => userInfo.value?.permissions || []);

    /**
     * 设置 token
     */
    function setToken(accessToken: string, refresh?: string) {
      token.value = accessToken;
      if (refresh) {
        refreshToken.value = refresh;
      }
    }

    /**
     * 设置用户信息
     */
    function setUserInfo(info: UserInfo) {
      userInfo.value = info;
    }

    /**
     * 登录
     * @param username - 用户名
     * @param password - 密码（原始密码，会自动 MD5 加密）
     * @param turnstileToken - Turnstile 验证 token
     */
    async function login(
      username: string,
      password: string,
      turnstileToken: string
    ) {
      try {
        // 使用 zod 验证登录参数
        const loginData = adminLoginBody.parse({
          username,
          password: encryptPassword(password), // MD5 加密密码
          turnstileToken,
        });

        // 调用登录 API
        const authAPI = getAdminAuth();
        const response = (await authAPI.adminLogin(
          loginData
        )) as unknown as AuthV1AdminLoginRes;

        // 保存 token
        if (response.accessToken) {
          setToken(response.accessToken, response.refreshToken);
        }

        // 保存用户信息
        if (response.admin) {
          const user: UserInfo = {
            id: response.admin.id || 0,
            username: response.admin.username || "",
            nickname: response.admin.realName || response.admin.username || "",
            email: response.admin.email,
            phone: response.admin.mobile,
            avatar: response.admin.avatar,
            roles: response.admin.roleName ? [response.admin.roleName] : [],
            permissions: response.admin.permissions || [],
          };
          setUserInfo(user);
        }

        return response;
      } catch (error) {
        console.error("[Login Error]:", error);
        throw error;
      }
    }

    /**
     * 登出
     */
    async function logout() {
      try {
        // 调用登出 API
        const authAPI = getAdminAuth();
        await authAPI.adminLogout({});
      } catch (error) {
        console.error("[Logout Error]:", error);
      } finally {
        // 清除本地状态
        token.value = "";
        refreshToken.value = "";
        userInfo.value = null;
      }
    }

    /**
     * 获取用户信息
     */
    async function getUserInfo() {
      try {
        // 调用获取用户信息 API
        const authAPI = getAdminAuth();
        const response =
          (await authAPI.adminGetProfile()) as unknown as AuthV1AdminUserInfo;

        // 更新用户信息
        const user: UserInfo = {
          id: response.id || 0,
          username: response.username || "",
          nickname: response.realName || response.username || "",
          email: response.email,
          phone: response.mobile,
          avatar: response.avatar,
          roles: response.roleName ? [response.roleName] : [],
          permissions: response.permissions || [],
        };
        setUserInfo(user);

        return userInfo.value;
      } catch (error) {
        console.error("[Get User Info Error]:", error);
        throw error;
      }
    }

    /**
     * 刷新 Access Token
     */
    async function refreshAccessToken() {
      try {
        // 调用刷新 token API
        const authAPI = getAdminAuth();
        const response = (await authAPI.adminTokenRefresh({
          refreshToken: refreshToken.value,
        })) as unknown as AuthV1AdminLoginRes;

        // 更新 token
        if (response.accessToken) {
          setToken(response.accessToken, response.refreshToken);
        }

        return token.value;
      } catch (error) {
        console.error("[Refresh Token Error]:", error);
        // 刷新失败，清除登录状态
        await logout();
        throw error;
      }
    }

    /**
     * 检查是否有指定权限
     */
    function hasPermission(permission: string): boolean {
      // 超级管理员拥有所有权限
      if (permissions.value.includes("*:*:*")) {
        return true;
      }
      return permissions.value.includes(permission);
    }

    /**
     * 检查是否有指定角色
     */
    function hasRole(role: string): boolean {
      return roles.value.includes(role);
    }

    return {
      // 状态
      token,
      refreshToken,
      userInfo,
      isLoggedIn,
      roles,
      permissions,

      // 方法
      setToken,
      setUserInfo,
      login,
      logout,
      getUserInfo,
      refreshAccessToken,
      hasPermission,
      hasRole,
    };
  },
  {
    // 持久化配置
    persist: {
      key: "luckytask-admin-user",
      storage: localStorage,
      paths: ["token", "refreshToken", "userInfo"],
    } as any,
  }
);
